/**
 * DEFCAT P6 — single tabbed catalogue editor
 * (`/defect-catalogues/:catalogueId`).
 *
 * Tabs: Overview · Trees ★ · Dependencies ★ · Versions · AI mapping ·
 * Structure. The header carries a version selector + status chip. Edits target
 * the selected version; only a DRAFT version is editable (published versions
 * render read-only with a "branch a draft" prompt from the Versions tab).
 */
import {
  useMemo,
  useState,
  type ReactElement,
  type SyntheticEvent,
} from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { PageHeading } from "@aeriosiq/ui";
import type { DefectCatalogueAdmin } from "@aeriosiq/types";
import {
  useAdminCataloguesQuery,
  useCatalogueVersionQuery,
  useCatalogueVersionsQuery,
} from "./taxonomyQueries.js";
import { OverviewTab } from "./OverviewTab.js";
import { TreesTab } from "./TreesTab.js";
import { DependenciesTab } from "./DependenciesTab.js";
import { VersionsTab } from "./VersionsTab.js";
import { AiMappingTab } from "./AiMappingTab.js";
import { StructureTab } from "./StructureTab.js";
import { useCatalogueWritePermissions } from "./useCatalogueWritePermissions.js";
import { CatalogueExportButton } from "./CatalogueExportButton.js";
import { locText } from "./locText.js";
import { ArrowLeftIcon } from "@aeriosiq/ui/icons";

type TabKey =
  | "overview"
  | "trees"
  | "dependencies"
  | "versions"
  | "ai"
  | "structure";

export function CatalogueEditorPage(): ReactElement {
  const { catalogueId = "" } = useParams<{ catalogueId: string }>();
  const [tab, setTab] = useState<TabKey>("trees");
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null,
  );

  const cataloguesQuery = useAdminCataloguesQuery();
  const versionsQuery = useCatalogueVersionsQuery(catalogueId);

  const catalogue: DefectCatalogueAdmin | null = useMemo(() => {
    const found = cataloguesQuery.data?.items.find(
      (c) => c.catalogueId === catalogueId,
    );
    // The list endpoint returns DefectCatalogue; the admin fields (scope /
    // visibility / version pointers) are optional in practice, so coerce
    // with sensible defaults for the baseline editor.
    if (!found) return null;
    return found as DefectCatalogueAdmin;
  }, [cataloguesQuery.data, catalogueId]);

  const versions = useMemo(
    () => versionsQuery.data?.items ?? [],
    [versionsQuery.data],
  );

  // Resolve the active version: explicit selection → open draft → latest.
  const activeVersionId = useMemo(() => {
    if (selectedVersionId !== null) return selectedVersionId;
    const draft = versions.find((v) => v.status === "draft");
    if (draft) return draft.versionId;
    const sorted = [...versions].sort((a, b) => b.versionNo - a.versionNo);
    return sorted[0]?.versionId ?? null;
  }, [selectedVersionId, versions]);

  const activeVersion = versions.find((v) => v.versionId === activeVersionId);

  // DL-04 — the version "Download catalogue (JSON)" exports: an explicit
  // pick, else the current PUBLISHED one (what a bundle.json names), else
  // the newest published, else what the editor shows.
  const exportVersionId = useMemo(() => {
    if (selectedVersionId !== null) return selectedVersionId;
    if (catalogue?.currentVersionId) return catalogue.currentVersionId;
    const published = versions
      .filter((v) => v.status === "published")
      .sort((a, b) => b.versionNo - a.versionNo);
    return published[0]?.versionId ?? activeVersionId;
  }, [selectedVersionId, catalogue, versions, activeVersionId]);

  // DEFCAT P6 — write-gating per the role matrix (scope-aware). READ is open
  // to any authenticated user; a draft is only EDITABLE when the user also
  // holds an edit role (tenant_admin/expert_analyst, or platform_admin for a
  // platform-scoped catalogue). The BE re-enforces this. See
  // useCatalogueWritePermissions.
  const { canEditDraft, canPublish } = useCatalogueWritePermissions(
    catalogue?.scope,
  );
  const editable = activeVersion?.status === "draft" && canEditDraft;

  const versionQuery = useCatalogueVersionQuery(catalogueId, activeVersionId);
  const structure = versionQuery.data;

  function handleTabChange(_e: SyntheticEvent, value: TabKey): void {
    setTab(value);
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        p: 3,
        gap: 1.5,
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <MuiLink
          component={RouterLink}
          to="/defect-catalogues"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            mb: 1,
          }}
        >
          <ArrowLeftIcon fontSize="small" /> All catalogues
        </MuiLink>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          rowGap={1}
        >
          <PageHeading>
            {catalogue ? locText(catalogue.name) : "Catalogue"}
          </PageHeading>
          {activeVersion ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CatalogueExportButton
                catalogueId={catalogueId}
                versionId={exportVersionId}
              />
              <Typography variant="body2" color="text.secondary">
                Version v{activeVersion.versionNo}
              </Typography>
              <Chip
                size="small"
                label={activeVersion.status}
                color={
                  activeVersion.status === "draft"
                    ? "warning"
                    : activeVersion.status === "published"
                      ? "success"
                      : "default"
                }
              />
            </Stack>
          ) : null}
        </Stack>
      </Box>

      <Tabs
        value={tab}
        onChange={handleTabChange}
        sx={{ flexShrink: 0, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Overview" value="overview" />
        <Tab label="Trees" value="trees" />
        <Tab label="Dependencies" value="dependencies" />
        <Tab label="Versions" value="versions" />
        <Tab label="AI mapping" value="ai" />
        <Tab label="Structure" value="structure" />
      </Tabs>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {cataloguesQuery.isLoading || versionsQuery.isLoading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress aria-label="Loading catalogue" />
          </Stack>
        ) : !catalogue ? (
          <Alert severity="error">Catalogue not found.</Alert>
        ) : tab === "overview" ? (
          <OverviewTab catalogue={catalogue} canEdit={canEditDraft} />
        ) : tab === "versions" ? (
          <VersionsTab
            catalogueId={catalogueId}
            versions={versions}
            selectedVersionId={activeVersionId}
            onSelectVersion={setSelectedVersionId}
            canBranch={canEditDraft}
            canPublish={canPublish}
          />
        ) : versionQuery.isLoading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress aria-label="Loading version" />
          </Stack>
        ) : !structure || activeVersionId === null ? (
          <Alert severity="info">
            No version structure available. Create a draft from the Versions
            tab.
          </Alert>
        ) : tab === "trees" ? (
          <TreesTab
            catalogueId={catalogueId}
            versionId={activeVersionId}
            structure={structure}
            editable={editable}
            classModel={catalogue.classModel}
          />
        ) : tab === "dependencies" ? (
          <DependenciesTab
            catalogueId={catalogueId}
            versionId={activeVersionId}
            structure={structure}
            editable={editable}
          />
        ) : tab === "ai" ? (
          <AiMappingTab
            catalogueId={catalogueId}
            versionId={activeVersionId}
            structure={structure}
            classModel={catalogue.classModel}
            editable={editable}
          />
        ) : (
          <StructureTab
            catalogueId={catalogueId}
            versionId={activeVersionId}
            structure={structure}
            editable={editable}
          />
        )}
      </Box>
    </Box>
  );
}
